<template>
  <div class="chemicals-categories-table">
    <v-progress-linear
      v-if="pending"
      indeterminate
    />

    <v-data-table
      v-if="response && response.enrichments"
      :headers="headers"
      :items="response.enrichments"
      id="chemicals-categories"
      :footer-props="{
        showFirstLastPage: true,
        itemsPerPageOptions: [10, 30, 50, -1],
      }"
      :items-per-page="10"
      :custom-sort="onSort"
    >
      <template #[`header.chemical`]="{}" />

      <template
        v-for="(h, i) in categoriesHeaders"
        #[`header.${h.value}`]="{ header }"
      >
        <div
          :key="h.value"
          class="rotate--90"
          :style="{
            color: i === 0 ? 'blue' : i === 1 || i === 2 ? 'red' : 'black',
          }"
        >
          {{ header.text }}
        </div>
      </template>

      <template #item="{ item }">
        <tr>
          <td
            :id="`a-3-${chemicalKey(item.chemical)}`"
            :class="{ 'selected-chemical-cell': chemicalKey(item.chemical) === selectedChemicalKey }"
            style="text-align: left"
          >
            <div
              v-if="showImages"
              style="display: inline-block; position: relative"
            >
              <button
                type="button"
                @click="gotoView2(item.chemical)"
                class="image-action-button"
                :aria-label="`View ${item.chemical.name || item.chemical.dtxsid}`"
              >
                <img
                  :src="`${TOXPRINTS_URL}/render?smiles=${encodeURIComponent(item.chemical.smiles)}`"
                  alt=""
                  style="width: 150px; height: 150px"
                />
              </button>
              <v-icon
                v-if="isInDSSTox(item.chemical)"
                class="top-right"
                color="green"
              >
                mdi-database-check
              </v-icon>
            </div>

            <div>
              <button
                type="button"
                @click="gotoView2(item.chemical)"
                class="cell-action-button"
              >
                <div class="pt-1 px-1">
                  {{ item.chemical.casrn || 'N/A' }}
                </div>
                <div class="pt-1 px-1 text-truncate-150">
                  {{ item.chemical.name || 'N/A' }}
                </div>
              </button>
            </div>
            <ChemPopover
              :chemical="item.chemical"
              :target="`a-3-${chemicalKey(item.chemical)}`"
              source="toxprints"
            />
          </td>
          <td
            v-for="(h, i) in categoriesHeaders"
            :key="i"
            :style="{
              backgroundColor: getColorCoding(
                item[h.value].length / response.categories.find((c) => c.category === h.value).count,
              ),
            }"
          >
            <button
              v-if="item[h.value].length"
              :id="`a-${chemicalKey(item.chemical)}-${i}`"
              type="button"
              @click="gotoView2(item.chemical)"
              class="cell-action-button"
            >
              {{ item[h.value].length }}
            </button>
            <b-popover
              v-if="item[h.value].length"
              :target="`a-${chemicalKey(item.chemical)}-${i}`"
              triggers="hover"
            >
              <a
                v-for="(a, j) in item[h.value]"
                :key="j"
                :href="`https://comptox.epa.gov/dashboard/assay_endpoints/${a}`"
                target="epa"
                style="display: block; white-space: nowrap"
              >
                {{ a }}
              </a>
            </b-popover>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { getDashboardUrl, TOXPRINTS_URL } from '@/main'
import ChemPopover from '@/components/chemical/ChemPopover/ChemPopover.vue'

export default {
  components: { ChemPopover },

  props: {
    chemicals: {
      type: Array,
      required: true,
    },
    selectedChemicalKey: {
      type: [String, Number],
      default: null,
    },
    showImages: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    pending: false,
    response: null,
  }),

  computed: {
    TOXPRINTS_URL() {
      return TOXPRINTS_URL
    },
    categoriesHeaders() {
      return this.response
        ? this.response.categories.map((c) => ({
            text: `${c.category} (${c.count})`,
            value: c.category,
          }))
        : []
    },
    headers() {
      return [{ text: '', value: 'chemical', sortable: false }, ...this.categoriesHeaders]
    },
  },

  methods: {
    chemicalKey(chemical) {
      return chemical?.id ?? chemical?.chemId ?? chemical?.sid ?? chemical?.key
    },
    onSort(items, sortBy, sortDesc) {
      if (sortBy.length) {
        items = items.sort((a, b) =>
          !sortDesc[0] ? b[sortBy[0]].length - a[sortBy[0]].length : a[sortBy[0]].length - b[sortBy[0]].length,
        )
      }
      return items
    },
    getDashboardUrl(sid) {
      return getDashboardUrl(sid)
    },
    gotoView2(chemical) {
      this.$emit('chemicalClicked', chemical)
    },
    getColorCoding(f) {
      if (f > 0.8) return 'red'
      if (f > 0.7) return 'orangered'
      if (f > 0.6) return 'tomato'
      if (f > 0.5) return 'coral'
      if (f > 0.4) return 'lightsalmon'
      if (f > 0.3) return 'peachpuff'
      if (f > 0.2) return 'moccasin'
      if (f > 0.1) return 'papayawhip'
      if (f > 0.0) return 'whitesmoke'
      return 'white'
    },
    isInDSSTox(item) {
      return item.cid || item.sid || item.name || item.casrn
    },
    getData() {
      if (this.chemicals && this.chemicals.length) {
        this.pending = true
        this.$axios
          .post(`${TOXPRINTS_URL}/chemicals_categories`, {
            chemicals: this.chemicals,
          })
          .then((response) => {
            this.response = response.data
            this.pending = false
          })
          .catch(() => {
            this.pending = false
          })
      }
    },
  },

  mounted() {
    this.getData()
  },
}
</script>
<style lang="scss">
.chemicals-categories-table {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  width: 100%;
}

#chemicals-categories {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  padding: 1px !important;

  > .v-data-table__wrapper {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
  }

  > .v-data-footer {
    flex: 0 0 auto;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: white;
  }

  thead {
    th {
      vertical-align: bottom;
      white-space: nowrap;
      max-width: 3em;
      height: 180px !important;
    }
  }

  tbody {
    td.selected-chemical-cell {
      background-color: rgba(24, 115, 205, 0.12) !important;
      box-shadow: inset 0 0 0 1px rgba(24, 115, 205, 0.45);
    }

    td {
      text-align: center;
      padding: 1px !important;
      border: 1px solid transparent;

      &:hover {
        background-color: beige !important;
      }

      a,
      .cell-action-button {
        display: block;
        width: 100%;
        height: 100%;
        text-decoration: none;
        color: black;
        padding-top: 1em;
      }
    }
  }

  table {
    th {
      text-align: center;
      height: 48px;

      border: 1px solid transparent !important;

      &:hover {
        background-color: beige !important;
      }

      a,
      .cell-action-button {
        display: block;
        width: 100%;
        height: 100%;
        text-decoration: none;
        color: black;
        padding-top: 1em;
      }
    }
  }
}

.image-action-button,
.cell-action-button {
  background: transparent;
  border: 0;
  cursor: pointer;
}

.image-action-button {
  padding: 0;
}

.cell-action-button {
  text-align: inherit;
}

.image-action-button:focus-visible,
.cell-action-button:focus-visible {
  outline: 2px solid #1873cd;
  outline-offset: 2px;
}
</style>
